import React, { createContext, useState, useEffect, useContext } from "react";
import { getReviews, type Review as ApiReview } from "../api/reviewsApi";

interface Review {
    status: string;
    id: string;
    name: string;
    description: string;
    img: string;
    rating: number;
    project_id?: string;
}

interface ReviewsContextType {
    reviews: Review[];
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
}

const ReviewsContext = createContext<ReviewsContextType>({
    reviews: [],
    loading: false,
    error: null,
    refresh: async () => {},
});

export const ReviewsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [reviews, setReviews] = useState<Review[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const transformApiReview = (apiReview: ApiReview): Review => {
        return {
            id: apiReview.id,
            name: apiReview.name,
            status: apiReview.status,
            description: apiReview.content,
            img: apiReview.avatar || '/images/placeholder.png',
            rating: apiReview.rating,
            project_id: apiReview.project_id
        };
    };

    const fetchReviews = async () => {
        setLoading(true);
        setError(null);
        try {
            const apiReviews = await getReviews();
            const transformedReviews = apiReviews.map(transformApiReview);
            setReviews(transformedReviews);
        } catch (err) {
            console.error("Ошибка при загрузке отзывов:", err);
            setError("Не удалось загрузить отзывы");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (reviews.length === 0) fetchReviews();
    }, []);

    return (
        <ReviewsContext.Provider value={{ reviews, loading, error, refresh: fetchReviews }}>
            {children}
        </ReviewsContext.Provider>
    );
};

export const useReviews = () => useContext(ReviewsContext);